<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <aside data-testid="app-sidebar" :class="sidebarClasses" class="fixed left-0 top-0 h-screen bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 z-30 flex flex-col">
    <!-- Logo Section -->
    <div class="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center gap-2">
        <div class="p-1.5 rounded-lg">
          <img :src="TCL_Logo" class="w-8 h-8" />
        </div>
        <span class="text-3xl font-extrabold text-[#227BE2]">TCL</span>
      </div>
      <button
        @click="toggleSidebar"
        type="button"
        data-testid="sidebar-collapse-btn"
        class="p-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-700 text-[#227BE2] transition-colors inline-flex items-center justify-center"
        title="Collapse Sidebar"
        aria-label="Collapse Sidebar"
      >
        <span class="w-5 h-5 inline-flex items-center justify-center rotate-180 [&>svg]:w-full [&>svg]:h-full [&>svg]:block" v-html="arrowSvg"></span>
      </button>
    </div>

    <!-- Search Section (Matching Dashboard design) -->
    <div class="px-3 pt-3 pb-1">
      <div class="relative">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          type="text"
          placeholder="Search..."
          class="w-full pl-8 pr-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700/60 text-gray-700 dark:text-gray-200 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#227BE2]"
        />
      </div>
    </div>

    <!-- Navigation -->
    <nav class="flex-1 overflow-y-auto py-3 px-3 space-y-1">
      <div v-for="(item, index) in menuItems" :key="index" class="mb-1">
        <!-- Menu Item with Submenu -->
        <div v-if="item.children">
          <button @click="toggleSubmenu(item.name)" :class="getMenuItemClass(item)" class="w-full">
            <component :is="item.icon" class="w-5 h-5 flex-shrink-0" />
            <span class="flex-1 text-left">{{ item.label }}</span>
            <svg :class="{ 'rotate-180': openSubmenus.includes(item.name) }" class="w-4 h-4 transition-transform text-current" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <!-- Submenu -->
          <Transition
            enter-active-class="transition-all duration-200"
            leave-active-class="transition-all duration-200"
            enter-from-class="opacity-0 max-h-0"
            leave-to-class="opacity-0 max-h-0"
            enter-to-class="opacity-100 max-h-96"
            leave-from-class="opacity-100 max-h-96"
          >
            <div v-if="openSubmenus.includes(item.name)" class="ml-4 mt-1 space-y-1 overflow-hidden">
              <router-link v-for="(child, childIndex) in item.children" :key="childIndex" :to="child.to" :class="getSubmenuItemClass(child)" @click="handleMenuClick">
                <span class="w-1.5 h-1.5 rounded-full bg-current flex-shrink-0"></span>
                <span>{{ child.label }}</span>
              </router-link>
            </div>
          </Transition>
        </div>

        <!-- Regular Menu Item -->
        <router-link v-else :to="item.to" :class="getMenuItemClass(item)" @click="handleMenuClick">
          <component :is="item.icon" class="w-5 h-5 flex-shrink-0" />
          <span>{{ item.label }}</span>
        </router-link>
      </div>
    </nav>

    <!-- User Profile Section at Bottom -->
    <div class="relative px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
      <div class="min-w-0 flex-1">
        <p class="text-xs font-semibold text-gray-900 dark:text-white truncate">{{ userName }}</p>
        <p class="text-[11px] text-gray-500 dark:text-gray-400 truncate">{{ userEmail }}</p>
      </div>

      <!-- Three-dot / Kebab Action Button -->
      <button
        ref="userMenuTriggerRef"
        @click="toggleUserMenu"
        type="button"
        class="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors inline-flex items-center justify-center flex-shrink-0"
        :class="{ 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white': isUserMenuOpen }"
        title="User menu"
        aria-label="User menu"
        :aria-expanded="isUserMenuOpen"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
          />
        </svg>
      </button>

      <!-- User Action Menu Popover (Matching Design Reference) -->
      <Transition
        enter-active-class="transition ease-out duration-150"
        enter-from-class="opacity-0 translate-y-2 scale-95"
        enter-to-class="opacity-100 translate-y-0 scale-100"
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100 translate-y-0 scale-100"
        leave-to-class="opacity-0 translate-y-2 scale-95"
      >
        <div
          v-if="isUserMenuOpen"
          ref="userMenuRef"
          class="absolute bottom-full right-3 mb-2 w-48 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700/80 overflow-hidden z-50 text-xs"
        >
          <!-- Top Section: Logout -->
          <div class="p-1.5">
            <button
              @click="handleLogout"
              type="button"
              class="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors text-left"
            >
              <IconLogout class="w-4 h-4 text-[#E5484D] flex-shrink-0" />
              <span>Logout</span>
            </button>
          </div>

          <!-- Divider Line -->
          <div class="border-t border-gray-100 dark:border-gray-700"></div>

          <!-- Bottom Section: Profile, Settings, Dark Mode -->
          <div class="p-1.5 space-y-0.5">
            <button
              @click="handleProfile"
              type="button"
              class="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors text-left"
            >
              <IconUser class="w-4 h-4 text-gray-600 dark:text-gray-300 flex-shrink-0" />
              <span>Profile</span>
            </button>

            <button
              @click="handleSettings"
              type="button"
              class="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors text-left"
            >
              <IconSettings class="w-4 h-4 text-gray-600 dark:text-gray-300 flex-shrink-0" />
              <span>Settings</span>
            </button>

            <button
              @click="handleToggleTheme"
              type="button"
              class="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors text-left"
            >
              <IconMoon class="w-4 h-4 text-gray-600 dark:text-gray-300 flex-shrink-0" />
              <span>Dark Mode</span>
            </button>
          </div>
        </div>
      </Transition>
    </div>
  </aside>

  <!-- Mobile Overlay -->
  <Transition enter-active-class="transition-opacity duration-300" leave-active-class="transition-opacity duration-300" enter-from-class="opacity-0" leave-to-class="opacity-0">
    <div v-if="isOpen" @click="closeSidebar" class="fixed inset-0 bg-black/25 dark:bg-black/50 z-20 lg:hidden"></div>
  </Transition>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, h, defineComponent } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useSidebar } from "@/composables/useSidebar";
import { useAuthStore } from "@/stores/auth";
import { useTheme } from "@/composables/useTheme";
import {
  IconSettings,
  IconInventoryManagement,
  IconWarehouseConfig,
  IconLogout,
  IconUser,
  IconMoon,
} from "@/components/icons";
import { TCL_Logo } from "@/assets/images/base/index.js";

// Import designated SVGs as raw text so they can inherit currentColor in the DOM
import dashboardSvg from "@/assets/images/sidebar/dashboard.svg?raw";
import inboundSvg from "@/assets/images/sidebar/inbound.svg?raw";
import outboundSvg from "@/assets/images/sidebar/outbound.svg?raw";
import financeSvg from "@/assets/images/sidebar/finance.svg?raw";
import noteSvg from "@/assets/images/sidebar/note.svg?raw";
import dataSvg from "@/assets/images/sidebar/data.svg?raw";
import arrowSvg from "@/assets/images/sidebar/arrow.svg?raw";

const route = useRoute();
const { isOpen, toggleSidebar, closeSidebar } = useSidebar();
const authStore = useAuthStore();

// Helper to create an inline SVG component that inherits currentColor
const makeSvgIcon = (rawSvg) =>
  defineComponent({
    name: "SidebarSvgIcon",
    render() {
      return h("span", {
        class: "w-5 h-5 flex-shrink-0 inline-flex items-center justify-center [&>svg]:w-full [&>svg]:h-full [&>svg]:block",
        innerHTML: rawSvg,
      });
    },
  });

let prevWidth = typeof window !== "undefined" ? window.innerWidth : 1024;
const handleResize = () => {
  const currentWidth = window.innerWidth;
  if (currentWidth < 1024) {
    if (prevWidth >= 1024) {
      closeSidebar();
    }
  } else {
    if (prevWidth < 1024) {
      isOpen.value = true;
    }
  }
  prevWidth = currentWidth;
};

onMounted(() => {
  handleResize();
  window.addEventListener("resize", handleResize);
  document.addEventListener("click", handleClickOutside);
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  document.removeEventListener("click", handleClickOutside);
  document.removeEventListener("keydown", handleKeydown);
});

const openSubmenus = ref(["inbound"]);

const allMenuItems = [
  {
    name: "dashboard",
    label: "Dashboard",
    icon: makeSvgIcon(dashboardSvg),
    to: "/",
  },
  {
    name: "inbound",
    label: "Inbound",
    icon: makeSvgIcon(inboundSvg),
    children: [
      { label: "ASN", to: "/inbound/asn" },
    ],
  },
  {
    name: "outbound",
    label: "Outbound",
    icon: makeSvgIcon(outboundSvg),
    children: [
      { label: "Delivery Orders", to: "/outbound/delivery-orders-list" },
    ],
  },
  {
    name: "inventory-management",
    label: "Inventory Management",
    icon: IconInventoryManagement,
    children: [
      { label: "Transfer Pallet", to: "/inventory-management/transfer-pallet" },
      { label: "Swap Pallet", to: "/inventory-management/swap-pallet" },
      { label: "Block Stock", to: "/inventory-management/block-stock" },
      { label: "Location Check", to: "/inventory-management/location-check" },
      { label: "Adjustment Pallet", to: "/inventory-management/adjustment-pallet" },
      { label: "Putaway Monitoring", to: "/inventory-management/putaway-monitoring" },
      { label: "Putback Monitoring", to: "/inventory-management/putback-monitoring" },
      { label: "Picking Monitoring", to: "/inventory-management/picking-monitoring" },
      { label: "Join Pallet", to: "/inventory-management/join-pallet" },
    ],
  },
  {
    name: "finance-report",
    label: "Finance Report",
    icon: makeSvgIcon(financeSvg),
    roles: ["finance", "superadmin"],
    children: [
      { label: "Customer Inventory Balance", to: "/reports/customer-inventory-balance", roles: ["finance", "superadmin"] },
      { label: "Occupancy Report", to: "/reports/occupancy-reports", roles: ["finance", "superadmin"] },
    ],
  },
  {
    name: "reports",
    label: "Report",
    icon: makeSvgIcon(noteSvg),
    children: [
      { label: "SOH", to: "/inventory/stock-on-hand" },
      { label: "SOH Damage Reports", to: "/reports/damage-reports" },
      { label: "SOH Blocked Reports", to: "/reports/blocked-reports" },
      { label: "Daily Inventory Summary", to: "/reports/master-data-item-reports" },
      { label: "Transaction History Inbound Reports", to: "/reports/transaction-history-inbound-reports" },
      { label: "Transaction History Outbound Reports", to: "/reports/transaction-history-outbound-reports" },
      { label: "LPN Report", to: "/reports/pallet-by-customer-reports" },
      { label: "Customer Room Location", to: "/reports/customer-room-location-reports" },
      { label: "LPN Discrepancy Reports", to: "/reports/lpn-discrepancy-reports" },
      { label: "Inventory Activity History", to: "/reports/stock-movement-reports" },
    ],
  },
  {
    name: "master data",
    label: "Master Data",
    icon: makeSvgIcon(dataSvg),
    children: [
      { label: "Address", to: "/master/address" },
      { label: "Customer", to: "/master/customer" },
      { label: "Company", to: "/master/company" },
      { label: "Item", to: "/master/item" },
      { label: "Location Management", to: "/master/location-management" },
      { label: "Origin Address", to: "/master/origin-address" },
      { label: "Product Group", to: "/master/product-group" },
      { label: "UOM", to: "/master/uom" },
      { label: "User", to: "/master/user" },
      { label: "Transporter", to: "/master/transporter" },
    ],
  },
  {
    name: "warehouse config",
    label: "Warehouse Config",
    icon: IconWarehouseConfig,
    children: [
      // WMS-33: Aging Config hidden from sidebar
      { label: "Full Pallet Config", to: "/master/full-pallet-config" },
      { label: "Gate", to: "/master/gate" },
      { label: "Rack Setting", to: "/master/rack-setting" },
      { label: "Warehouse", to: "/master/warehouse" },
    ],
  },
];

// Filtered menu items based on user role
const menuItems = computed(() => {
  const userRole = authStore.userRole?.toLowerCase();
  const isAdmin = authStore.isAdmin;
  const allowedItems = authStore.allowedSidebarItems;

  const filterChildren = (children) => {
    if (!children) return undefined;
    return children.filter((child) => {
      if (child.roles && !child.roles.includes(userRole)) {
        return false;
      }
      return true;
    });
  };

  const filterItem = (item) => {
    if (item.roles && !item.roles.includes(userRole)) {
      return false;
    }
    return true;
  };

  // If user is admin, show all menu items that pass item.roles
  if (isAdmin) {
    return allMenuItems
      .filter(filterItem)
      .map((item) => ({
        ...item,
        children: filterChildren(item.children),
      }))
      .filter((item) => !item.children || item.children.length > 0);
  }

  // If null, show all (shouldn't happen for non-admin, but safety check)
  if (allowedItems === null) {
    return allMenuItems
      .filter(filterItem)
      .map((item) => ({
        ...item,
        children: filterChildren(item.children),
      }))
      .filter((item) => !item.children || item.children.length > 0);
  }

  // Filter menu items based on allowed items
  return allMenuItems
    .filter((item) => allowedItems.includes(item.name))
    .filter(filterItem)
    .map((item) => ({
      ...item,
      children: filterChildren(item.children),
    }))
    .filter((item) => !item.children || item.children.length > 0);
});

const toggleSubmenu = (name) => {
  const index = openSubmenus.value.indexOf(name);
  if (index > -1) {
    openSubmenus.value.splice(index, 1);
  } else {
    openSubmenus.value.push(name);
  }
};

const handleMenuClick = () => {
  // Close sidebar on mobile/tablet (below lg breakpoint - 1024px)
  if (window.innerWidth < 1024) {
    closeSidebar();
  }
};

const sidebarClasses = computed(() => {
  return isOpen.value ? "w-64" : "-translate-x-full w-64";
});

// Check if an item (or any of its children) matches the current route
const isItemActive = (item) => {
  if (item.to && route.path === item.to) {
    return true;
  }
  if (
    item.children &&
    item.children.some(
      (child) => route.path === child.to || route.path.startsWith(child.to + "/")
    )
  ) {
    return true;
  }
  return false;
};

const getMenuItemClass = (item) => {
  const active = isItemActive(item);
  const baseClasses =
    "flex items-center gap-3 px-3.5 py-2.5 rounded-xl transition-all text-sm font-medium";
  const activeClasses = active
    ? "bg-[#227BE2] text-white shadow-sm"
    : "text-[#227BE2] hover:bg-blue-50/60 dark:hover:bg-gray-700/60";

  return `${baseClasses} ${activeClasses}`;
};

const getSubmenuItemClass = (item) => {
  const isActive = route.path === item.to || route.path.startsWith(item.to + "/");
  const baseClasses =
    "flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm font-medium";
  const activeClasses = isActive
    ? "text-white bg-[#227BE2] shadow-xs"
    : "text-[#227BE2] hover:bg-blue-50/60 dark:hover:bg-gray-700/50";

  return `${baseClasses} ${activeClasses}`;
};

// User Profile Information
const userName = computed(() => {
  return (
    authStore.currentUser?.name ||
    authStore.currentUser?.username ||
    "System Admin"
  );
});

const userEmail = computed(() => {
  return authStore.currentUser?.email || "systemadmin123@gmail.com";
});

// User Action Menu
const router = useRouter();
const { toggleTheme } = useTheme();

const isUserMenuOpen = ref(false);
const userMenuRef = ref(null);
const userMenuTriggerRef = ref(null);

const toggleUserMenu = () => {
  isUserMenuOpen.value = !isUserMenuOpen.value;
};

const closeUserMenu = () => {
  isUserMenuOpen.value = false;
};

const handleLogout = async () => {
  closeUserMenu();
  await authStore.logout();
};

const handleProfile = () => {
  closeUserMenu();
  router.push('/profile');
};

const handleSettings = () => {
  closeUserMenu();
  router.push('/settings');
};

const handleToggleTheme = () => {
  toggleTheme();
  closeUserMenu();
};

const handleKeydown = (event) => {
  if (event.key === 'Escape') {
    closeUserMenu();
  }
};

const handleClickOutside = (event) => {
  if (
    isUserMenuOpen.value &&
    userMenuRef.value &&
    !userMenuRef.value.contains(event.target) &&
    userMenuTriggerRef.value &&
    !userMenuTriggerRef.value.contains(event.target)
  ) {
    closeUserMenu();
  }
};

watch(isOpen, (newVal) => {
  if (!newVal) {
    closeUserMenu();
  }
});

watch(
  () => route.path,
  () => {
    closeUserMenu();
  }
);
</script>
